import { ScrollToApplyButton } from '#/components/ApplyButton/ScrollToApplyButton';
import { Divider } from '#/components/Divider/Divider';
import { Text } from '#/components/Text/Text';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ListingTiming } from '#/modules/listing/helpers/listingTiming';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { colors } from '#/theme/theme';
import { timeago } from '#/utils/string/datetime/timeago';

import { ListingViewSidebarContent_Hide_1 } from './ListingViewSidebarContent.styled';

const APPLY_EVENT_NAME = {
  JOB: 'Apply Button on Job Listing (Sidebar)',
  INTERNSHIP: 'Apply Button on Internship Listing (Sidebar)',
  VOLOP: 'I Want to Help Button on Volop Listing (Sidebar)',
  EVENT: 'Join Button on Event Listing (Sidebar)',
};

type ListingSidebarContentProps = {
  timing: ListingTiming;
  listingViewStatus: ListingViewStatus;
  showScrollToApplyButton: boolean;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  applied: boolean;
};

export function ListingViewSidebarContent({
  timing,
  listingViewStatus,
  showScrollToApplyButton,
  listingType,
  applied,
}: ListingSidebarContentProps) {
  const { i18n, locale } = useI18n();

  const applyEventName = APPLY_EVENT_NAME[listingType];

  return (
    <>
      {timing.publishedDate && (
        <div>
          {timing.isNew && (
            <>
              <Text
                style={{
                  borderBottom: `4px solid ${colors.brandYellow}`,
                  fontWeight: 'semiBold',
                  color: colors.mediumContentGrey,
                  display: 'inline-block',
                  lineHeight: '6px',
                  marginLeft: '0px',
                  marginRight: '12px',
                  padding: '0px',
                }}
              >
                {i18n('New')}
              </Text>
              <Text
                style={{
                  display: 'inline-block',
                  color: colors.lightContentGrey,
                  paddingLeft: 2,
                  paddingRight: 2,
                }}
              >
                |
              </Text>
            </>
          )}
          <Text
            style={{
              display: 'inline-block',
              color: colors.lightContentGrey,
            }}
          >
            {
              /* TRANSLATORS: Listing page - time published */
              i18n('Published %(timeAgo)s', {
                timeAgo: timeago(timing.publishedDate, {
                  now: timing.now,
                  locale,
                }),
              })
            }
          </Text>
        </div>
      )}

      {listingType !== 'EVENT' &&
        listingViewStatus !== ListingViewStatus.EXPIRED &&
        showScrollToApplyButton && (
          <ListingViewSidebarContent_Hide_1>
            <Tooltip
              content={i18n("You can't take this action in Preview Mode")}
              triggerCondition={listingViewStatus === ListingViewStatus.PREVIEW}
              variant="filled"
              placement="bottom-center"
              size="large"
            >
              <div
                style={{
                  marginTop: 24,
                  cursor:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'not-allowed'
                      : undefined,
                  pointerEvents:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'none'
                      : undefined,
                }}
              >
                <ScrollToApplyButton
                  tabIndex={
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? -1
                      : undefined
                  }
                  applied={applied}
                  eventName={applyEventName}
                  qaId="easy-apply-button-sidebar"
                >
                  {listingType === 'VOLOP' ? i18n('I Want to Help') : undefined}
                </ScrollToApplyButton>
              </div>
            </Tooltip>
          </ListingViewSidebarContent_Hide_1>
        )}

      <Divider marginTop={24} marginBottom={24} />
    </>
  );
}
